// i18n: every user-facing string lives in src/locales/*. Resolution order per
// TECH_SPEC §4.4: ?lang= > navigator.language (zh* → zh) > en.

import { en, type Dict } from './locales/en';
import { zh } from './locales/zh';

export type Locale = 'zh' | 'en';

/** Keys whose value is a single string. */
export type TextKey = {
  [K in keyof Dict]: Dict[K] extends string ? K : never;
}[keyof Dict];

/** Keys whose value is a copy pool (design §4.6). */
export type PoolKey = {
  [K in keyof Dict]: Dict[K] extends readonly string[] ? K : never;
}[keyof Dict];

export type Params = Readonly<Record<string, string | number>>;

const DICTS: Record<Locale, Dict> = { zh, en };

let current: Locale = 'en';
let dict: Dict = en;

export function detectLocale(search = location.search, nav = navigator.language): Locale {
  const q = new URLSearchParams(search).get('lang');
  if (q) {
    const norm = normalize(q);
    if (norm) return norm;
  }
  return normalize(nav) ?? 'en';
}

function normalize(tag: string): Locale | null {
  const t = tag.toLowerCase();
  if (t.startsWith('zh')) return 'zh';
  if (t.startsWith('en')) return 'en';
  return null;
}

export function setLocale(locale: Locale): void {
  current = locale;
  dict = DICTS[locale];
  document.documentElement.lang = locale === 'zh' ? 'zh-Hans' : 'en';
}

export function getLocale(): Locale {
  return current;
}

export function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => {
    const v = params[key];
    return v === undefined ? whole : String(v);
  });
}

/** Single string lookup. */
export function t(key: TextKey, params?: Params): string {
  const raw = dict[key] as string | undefined;
  const fallback = en[key] as string;
  return interpolate(raw ?? fallback, params);
}

/** Deterministic pick from a copy pool — same run always shows the same line. */
export function pick(key: PoolKey, roll: () => number, params?: Params): string {
  const pool = (dict[key] ?? en[key]) as readonly string[];
  const list = pool.length > 0 ? pool : (en[key] as readonly string[]);
  const idx = Math.min(list.length - 1, Math.max(0, Math.floor(roll() * list.length)));
  return interpolate(list[idx] ?? '', params);
}
